// HQ — the home screen.
//
// SIMS-REBOOT: the voxel world IS the game. The main screen shows exactly three
// things the user asked for: my money, my freedom meter, and what to do next.
// Everything else — cashflow statement, energy, mindset, decisions, ventures
// list, stats — has been moved off the main view. The world's buildings are the
// navigation (tap the stand to enter the trading week, tap a venture shop to
// enter that venture). Modals queue, never stack. Mentors/dreams/quiz surface
// contextually via watchers, not on a timer.

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useGameState,
  useDispatch,
  selectCash,
  selectStreak,
  selectLevel,
} from '@biz-kid/store';
import { ThemeToggle } from '../../ui/theme.js';
import { money, Bar, empireStage } from '../../ui/kit.js';
import { CountUp } from '../../ui/CountUp.js';
import { CartoonMiko, CartoonWorld } from '../../ui/cartoon.js';
import {
  mastermindUnlocked,
  businessWeeklyResult,
  findBusiness,
  repFactor,
  dayKeyFromDate,
} from '@biz-kid/engine';
import { builderContent } from '@biz-kid/engine/content/builder';
import { useLife, useLifeComputed, useLifeDispatch } from '../../life/store.js';
import { useMentorAdvice } from '../../life/useMentorAdvice.js';
import { useKnowledgePending, KnowledgeCheckModal } from '../../life/KnowledgeCheck.js';
import { useModalSlot } from '../../ui/modalQueue.js';
import { MicButton } from '../../voice/MicButton.js';
import { useVoice } from '../../voice/VoiceContext.js';
import { hasHeardGuide } from '../../voice/voiceGuide.js';

// Dark-neon studio shell tokens — deep-purple panels with a glowing neon border,
// light text. Shares the dark LED-room look with the world art and the chrome.
const INK = '#0b0a16'; // text on bright neon (BigAction) only
const TEXT = '#f4f1ff';
const TEXT_2 = '#cbc2ec';
const NEON = '#37b6ff';
const cartoonPanel: React.CSSProperties = {
  background: '#241b42',
  border: `2.5px solid ${NEON}`,
  borderRadius: 18,
  boxShadow: `0 0 0 1px ${NEON}66, 0 0 18px ${NEON}55, 0 6px 18px rgba(0,0,0,0.5)`,
  color: TEXT,
};

export type HQProps = {
  morningDone: boolean;
  showRaise: boolean;
  onStartMorning: () => void;
  onOpenStand: () => void;
  onRaise: () => void;
  onFreedom: () => void;
  onVision: () => void;
  onMastermind: () => void;
  onServices: () => void;
  onSocial: () => void;
  onGaming: () => void;
  onTrade: () => void;
  onProperty: () => void;
  onBroker: () => void;
  onEndOfYear: () => void;
};

export function HQ({
  morningDone, showRaise, onStartMorning, onOpenStand, onRaise,
  onFreedom, onMastermind, onServices, onSocial, onGaming, onTrade, onProperty, onBroker, onEndOfYear,
}: HQProps) {
  const lifeAge = useLife().age;
  const life = useLife();
  const computed = useLifeComputed();
  const lifeDispatch = useLifeDispatch();
  // The Mentor's Cottage in the world glows when a recruited mentor has fresh
  // advice this week OR a Knowledge Check is pending. Tap routes to whichever
  // is waiting (KC takes priority — it's the rarer event).
  const { hasFreshAdvice } = useMentorAdvice();
  const pendingKC = useKnowledgePending();
  const showMentorCottage = mastermindUnlocked(lifeAge);
  const cottageGlow = hasFreshAdvice || !!pendingKC;
  const [kcOpen, setKcOpen] = useState(false);
  const kcSlot = useModalSlot('knowledge-check', kcOpen && !!pendingKC, 60);

  const cash = useGameState(selectCash);
  const streak = useGameState(selectStreak);
  const level = useGameState(selectLevel);
  const dayOfWeek = useGameState((s) => s.clock.dayOfWeek);

  const servicesActive = useGameState((s) => s.services.active);
  const socialActive = useGameState((s) => s.social.active);
  const gamingActive = useGameState((s) => s.gaming.active);
  const tradeActive = useGameState((s) => s.trade.active);
  const hasStand = useGameState((s) => s.businesses.length > 0);
  const business = useGameState((s) => s.businesses[0]);
  const assetCount = useLife().assets.length;
  const ledger = useGameState((s) => s.ledger);

  // ── World event keys — bump a monotonic counter when something happened, so
  // the world plays a transient animation. Detection via refs so we only fire
  // on the change, not every render. ────────────────────────────────────────
  const [deliveryKey, setDeliveryKey] = useState(0);
  const [rainKey, setRainKey] = useState(0);
  const prevAssetCountRef = useRef(assetCount);
  useEffect(() => {
    if (assetCount > prevAssetCountRef.current) setDeliveryKey((k) => k + 1);
    prevAssetCountRef.current = assetCount;
  }, [assetCount]);
  // Rain cloud fires when the latest settled week was a loss week. Ref-diff so
  // we play it exactly once per settle, not every render.
  const prevLossWeekRef = useRef<number | null>(null);
  const weekState = useGameState((s) => s.clock.week);
  useEffect(() => {
    const settledWeek = weekState - 1;
    if (settledWeek < 1) return;
    if (prevLossWeekRef.current === settledWeek) return;
    prevLossWeekRef.current = settledWeek;
    const cf = ledger
      .filter((t) => t.week === settledWeek)
      .reduce((s, t) => (t.category === 'capital' ? s : s + t.amount), 0);
    if (cf < 0) setRainKey((k) => k + 1);
  }, [weekState, ledger]);

  // Stand tap — first ever flips the onboarding flag (kills the hint), then
  // navigates. Subsequent taps just navigate (with the sales-juice pop, which
  // the world handles internally).
  const handleOpenStand = () => {
    if (!life.firstStandTapped) lifeDispatch({ type: 'MARK_FIRST_STAND_TAP' });
    onOpenStand();
  };
  const hintTapStand = hasStand && !life.firstStandTapped;

  const tradeOpen = lifeAge >= 13;
  const servicesOpen = lifeAge >= 12;
  const socialOpen = lifeAge >= 13;
  const gamingOpen = lifeAge >= 14;
  const brokerOpen = lifeAge >= 14; // deals need a name people trust
  const propertyOpen = lifeAge >= 15; // biggest capital — the late-game venture

  // ── The Smart CTA — exactly one "what to do next" prompt. ─────────────────
  // Priority order: morning ritual → raise money (no stand yet) → open the
  // stand (week not yet run) → end the year (week settled). Falls through to
  // a calm default when there's nothing pressing.
  const cta = useMemo(() => {
    // Morning is handled by the forced gate in DailyHub — no CTA needed for it.
    if (!hasStand) {
      return { label: '🔥 Open your Drop Store', sub: 'Your first drop is waiting.', action: onRaise, tone: 'primary' as const };
    }
    if (dayOfWeek < 5) {
      return { label: '🔥 Drop this week', sub: 'Set your price, sell out.', action: onOpenStand, tone: 'primary' as const };
    }
    // dayOfWeek === 5: week settled. End-of-Year is the next big move every
    // ~52 weeks. We surface it as the soft CTA — taps the natural rhythm.
    return { label: '⬆️ LEVEL UP', sub: 'NEW SEASON. LET’S GO.', action: onEndOfYear, tone: 'up' as const };
  }, [hasStand, dayOfWeek, onRaise, onOpenStand, onEndOfYear]);
  // morningDone + onStartMorning now drive the optional morning-boost chip.

  // A friendly one-liner from Miko — gives the home a voice instead of a
  // spreadsheet. Confident, not cutesy (skewed to the 13-year-old end).
  const greeting = useMemo(() => {
    if (computed.freedomMode) return "YOU'RE FREE. LEGEND. 👑";
    if (computed.freedomPct >= 50) return 'HALFWAY THERE. STACK IT.';
    if (streak >= 3) return `${streak}-DAY STREAK. 🔥 LET'S GO.`;
    if (!hasStand) return "LET'S GET IT.";
    return "STORE'S HOT RIGHT NOW. 🔥";
  }, [computed.freedomMode, computed.freedomPct, streak, hasStand]);

  // The studio belongs to the PLAYER — their name goes on it. Falls back to
  // "BOSS" for saves that somehow skip the NameGate.
  const playerName = life.playerName ?? 'Boss';

  // MIKO SPEAKS — greet aloud once when the studio (home) opens. Respects the
  // voice setting; no-op if the kid turned voice off. On the very first-ever
  // visit the VoiceCoach plays the full "welcome to your studio" explainer
  // instead — speaking both would cancel each other mid-sentence.
  const { speak: voiceSpeak } = useVoice();
  const spokeGreetingRef = useRef(false);
  // Frozen at render time — BEFORE VoiceCoach's effect marks 'hub' heard. If
  // the explainer is about to play this mount, the greeting stays silent.
  const hubHeardAtMount = useRef(hasHeardGuide('hub'));
  useEffect(() => {
    if (spokeGreetingRef.current || !hubHeardAtMount.current) return;
    spokeGreetingRef.current = true;
    void voiceSpeak(`${playerName}, your studio is live. ${greeting}`);
  }, [voiceSpeak, greeting, playerName]);

  return (
    <div
      className="bk-app"
      style={{ position: 'fixed', inset: 0, zIndex: 5, overflowY: 'auto' }}
    >
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 'clamp(16px, 4vw, 26px)', paddingBottom: 90 }}>
        {/* Greeting — Miko in a bold cartoon panel + a one-liner. */}
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: 16, flexShrink: 0, overflow: 'hidden',
              background: '#241b42', border: `2.5px solid ${NEON}`,
              boxShadow: `0 0 14px ${NEON}66`,
              display: 'grid', placeItems: 'center',
            }}
          >
            <CartoonMiko size={50} mood="happy" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 900, fontSize: 20, color: TEXT, lineHeight: 1.15 }}>{playerName.toUpperCase()}'S STUDIO IS LIVE 🔴</div>
            <div style={{ fontSize: 13.5, color: TEXT_2, fontWeight: 700, marginTop: 1 }}>{greeting}</div>
          </div>
          {streak > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 999, background: '#ffba2b', color: INK, border: `2px solid #ffba2b`, boxShadow: '0 0 12px #ffba2b66', fontWeight: 900, fontSize: 15, flexShrink: 0 }}>
              🔥 {streak}
            </div>
          )}
          <ThemeToggle />
        </header>

        {/* Optional morning boost — a quick buff, never a wall. Tap to take a
            breath + intention for a focus edge; ignore it and just play. */}
        {hasStand && !morningDone && (
          <motion.button
            type="button"
            onClick={onStartMorning}
            whileTap={{ scale: 0.98 }}
            style={{
              width: '100%', marginBottom: 12, padding: '10px 14px', borderRadius: 14,
              border: '2px solid #ffba2b', background: 'rgba(255,186,43,0.12)', color: '#ffd06a',
              boxShadow: '0 0 12px #ffba2b33', cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 900 }}>☀️ GRAB YOUR MORNING BOOST</span>
            <span style={{ fontSize: 12, fontWeight: 800, opacity: 0.85 }}>TAP →</span>
          </motion.button>
        )}

        {/* Always-on goal — the one thing you're working toward. */}
        <GoalStrip cash={cash} freedomPct={computed.freedomPct} hasStand={hasStand} />

        {/* The town — bold cartoon panel. Buildings are tap-targets. */}
        <div style={{ ...cartoonPanel, padding: 0, overflow: 'hidden', marginBottom: 14 }}>
          <CartoonWorld
            shops={{
              services: servicesActive,
              social: socialActive,
              gaming: gamingActive,
              trade: tradeActive,
              vendingMachine: !showRaise,
              property: propertyOpen,
              broker: brokerOpen,
            }}
            hasStand={hasStand}
            showMentorCottage={showMentorCottage}
            hasMentorAdvice={cottageGlow}
            hintTapStand={hintTapStand}
            deliveryKey={deliveryKey}
            rainKey={rainKey}
            stageLabel={empireStage(level)}
            cashLabel={money(cash)}
            onOpenStand={handleOpenStand}
            onServices={servicesOpen ? onServices : undefined}
            onSocial={socialOpen ? onSocial : undefined}
            onGaming={gamingOpen ? onGaming : undefined}
            onTrade={tradeOpen ? onTrade : undefined}
            onProperty={propertyOpen ? onProperty : undefined}
            onBroker={brokerOpen ? onBroker : undefined}
            onRaise={onRaise}
            onMastermind={() => {
              if (pendingKC) setKcOpen(true);
              else onMastermind();
            }}
          />
        </div>

        {/* The play loop: with a business, one price choice + one tap runs the
            whole week to payday. Before that, the single "fund it" CTA. */}
        {hasStand && business ? (
          <WeekRunner business={business} onTune={handleOpenStand} />
        ) : (
          <BigAction cta={cta} />
        )}

        {/* Two friendly stat cards — bold cartoon panels, big numbers. */}
        <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
          <motion.button
            type="button"
            onClick={onFreedom}
            whileTap={{ scale: 0.97 }}
            style={{ ...cartoonPanel, flex: 1, minWidth: 0, padding: 16, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <div style={{ fontSize: 13, fontWeight: 900, color: TEXT_2 }}>💰 MY STACK</div>
            <CountUp
              value={cash}
              format={(n) => money(n)}
              className="bk-tnum"
              style={{ display: 'block', fontSize: 30, fontWeight: 900, color: TEXT, lineHeight: 1.1, marginTop: 4 }}
            />
          </motion.button>
          <motion.button
            type="button"
            onClick={onFreedom}
            whileTap={{ scale: 0.97 }}
            style={{ ...cartoonPanel, flex: 1, minWidth: 0, padding: 16, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', ...(computed.freedomMode ? { borderColor: '#2fe08a', boxShadow: '0 0 0 1px #2fe08a66, 0 0 18px #2fe08a66, 0 6px 18px rgba(0,0,0,0.5)' } : {}) }}
          >
            <div style={{ fontSize: 13, fontWeight: 900, color: computed.freedomMode ? '#2fe08a' : TEXT_2 }}>
              {computed.freedomMode ? '🔓 FREE!' : '🎯 FREEDOM LEVEL'}
            </div>
            <CountUp
              value={computed.freedomPct}
              format={(n) => `${Math.round(n)}%`}
              className="bk-tnum"
              style={{ display: 'block', fontSize: 30, fontWeight: 900, color: computed.freedomMode ? '#2fe08a' : NEON, lineHeight: 1.1, marginTop: 4 }}
            />
            <div style={{ marginTop: 8 }}>
              <Bar frac={Math.min(1, computed.freedomPct / 100)} color={computed.freedomMode ? '#2fe08a' : NEON} height={8} />
            </div>
          </motion.button>
        </div>

      </div>

      {/* Knowledge Check modal — opens only when the kid taps the glowing
          cottage AND the queue gives us the slot. Closing it dismisses for
          this session; the answer is recorded in life store either way. */}
      {kcOpen && pendingKC && kcSlot.canShow && (
        <KnowledgeCheckModal pending={pendingKC} onClose={() => setKcOpen(false)} />
      )}

      {/* Talk-to-Miko mic — glowing floating control, states: listening/thinking/speaking. */}
      <MicButton />
    </div>
  );
}

/** The one big do-this-now action. Huge, rounded, can't-miss — the focal point
 *  of the home screen. Swaps with a soft cross-fade when the next action
 *  changes. */
function BigAction({ cta }: { cta: { label: string; sub: string; action: () => void; tone: 'primary' | 'up' } }) {
  // Bright neon button on the dark room — saturated fill, glowing border, dims
  // its glow on tap ("presses in").
  const bg = cta.tone === 'up' ? '#2fe08a' : '#ffba2b';
  return (
    <AnimatePresence mode="wait">
      <motion.button
        key={cta.label}
        type="button"
        onClick={cta.action}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.22 }}
        whileTap={{ y: 1, boxShadow: `0 0 0 1px ${bg}66, 0 0 8px ${bg}66` }}
        style={{
          width: '100%',
          marginTop: 14,
          padding: '16px 20px',
          borderRadius: 18,
          border: `2.5px solid ${bg}`,
          background: bg,
          color: INK,
          boxShadow: `0 0 0 1px ${bg}66, 0 0 22px ${bg}77, 0 8px 20px rgba(0,0,0,0.5)`,
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 900, fontSize: 20, lineHeight: 1.15 }}>{cta.label}</span>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, opacity: 0.8, marginTop: 2 }}>{cta.sub}</span>
        </span>
        <span style={{ fontSize: 30, fontWeight: 900, flexShrink: 0 }}>→</span>
      </motion.button>
    </AnimatePresence>
  );
}

/** Always-on "what am I working toward" strip — one clear north-star goal with
 *  a progress bar, so the player is never lost. */
function GoalStrip({ cash, freedomPct, hasStand }: { cash: number; freedomPct: number; hasStand: boolean }) {
  let label: string;
  let hint: string;
  let frac: number;
  if (!hasStand) {
    label = '🎯 START YOUR EMPIRE';
    hint = 'TAP BELOW';
    frac = 0;
  } else if (freedomPct < 1) {
    const target = 500; // first money-maker (the vending machine asset)
    label = `🎯 STACK ${money(target)} → MONEY MACHINE`;
    hint = `${money(cash)} STACKED`;
    frac = Math.min(1, cash / target);
  } else {
    const next = [25, 50, 75, 100].find((m) => freedomPct < m) ?? 100;
    label = `🎯 HIT ${next}% FREEDOM`;
    hint = `${Math.round(freedomPct)}% THERE`;
    frac = Math.min(1, freedomPct / next);
  }
  return (
    <div style={{ ...cartoonPanel, padding: '11px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 13.5, fontWeight: 900, color: TEXT, lineHeight: 1.2 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 800, color: TEXT_2, flexShrink: 0 }}>{hint}</span>
      </div>
      <div style={{ marginTop: 8 }}>
        <Bar frac={frac} color={NEON} height={10} />
      </div>
    </div>
  );
}

type Biz = { id: string; defId: string; price: number; marketingSpend: number };

/** The play loop, collapsed to one decision + one tap. Pick a price (the supply
 *  & demand lesson, live), then "Sell this week!" runs the whole week to payday
 *  in one go — the existing Payday modal delivers the reward + keep/save/invest
 *  choice. Depth (ads, day-by-day, the demand curve) lives behind "Tune". */
function WeekRunner({ business, onTune }: { business: Biz; onTune: () => void }) {
  const dispatch = useDispatch();
  const reputation = useGameState((s) => s.profile.reputation);
  const dayOfWeek = useGameState((s) => s.clock.dayOfWeek);
  const dayKey = useGameState((s) => s.clock.lastWallClockDayKey);
  const def = findBusiness(builderContent, business.defId)!;
  const pMin = Math.max(1, Math.round(def.sweetPrice * 0.4));
  const pMax = Math.round(def.sweetPrice * 1.8);
  const price = business.price;
  const res = businessWeeklyResult({
    baseDemand: def.baseDemand,
    price,
    sweetPrice: def.sweetPrice,
    marketingSpend: business.marketingSpend,
    qualityFactor: def.qualityFactorDefault,
    repFactor: repFactor(reputation),
    capacity: def.capacity,
    cogsPerUnit: def.cogsPerUnit,
    fixedPerWeek: def.fixedPerWeek,
  });
  const setPrice = (p: number) =>
    dispatch({ kind: 'SetPrice', businessId: business.id, price: Math.max(pMin, Math.min(pMax, p)) });

  const [running, setRunning] = useState(false);
  const [burst, setBurst] = useState<number | null>(null);
  const sell = () => {
    if (running) return;
    setRunning(true);
    const gained = res.activeIncome; // the profit the kid was just shown
    // From dayOfWeek d, exactly (6 - d) AdvanceDays run the rest of the week and
    // settle once (settle fires on the advance from day 5). The settle pops the
    // Payday modal (ProfitHarvest), which waits ~1.2s so this burst plays first.
    const advances = Math.max(1, 6 - dayOfWeek);
    const d = new Date(`${dayKey}T00:00:00Z`);
    for (let i = 0; i < advances; i++) {
      d.setUTCDate(d.getUTCDate() + 1);
      dispatch({ kind: 'AdvanceDay', dayKey: dayKeyFromDate(d) });
    }
    if (gained > 0) {
      setBurst(gained);
      setTimeout(() => setBurst(null), 1400);
    }
    setTimeout(() => setRunning(false), 700);
  };

  const profit = res.activeIncome;
  return (
    <div style={{ ...cartoonPanel, padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 900, color: TEXT_2 }}>SET YOUR PRICE</span>
        <button
          type="button"
          onClick={onTune}
          style={{ background: 'transparent', border: 'none', color: NEON, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}
        >
          TUNE ⚙️
        </button>
      </div>

      {/* Price stepper — big, two taps, with the live supply/demand readout. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, margin: '12px 0 6px' }}>
        <StepBtn label="−" onClick={() => setPrice(price - 1)} disabled={price <= pMin} />
        <div style={{ textAlign: 'center', minWidth: 120 }}>
          <div className="bk-tnum" style={{ fontSize: 36, fontWeight: 900, color: TEXT, lineHeight: 1 }}>{money(price)}</div>
          <div style={{ fontSize: 12.5, color: TEXT_2, fontWeight: 800, marginTop: 3 }}>{res.unitsSold} FANS COP IT</div>
        </div>
        <StepBtn label="+" onClick={() => setPrice(price + 1)} disabled={price >= pMax} />
      </div>
      <div style={{ textAlign: 'center', fontSize: 13.5, fontWeight: 900, color: profit >= 0 ? '#2fe08a' : '#ff5c8a', marginBottom: 12 }}>
        {profit >= 0 ? '+' : ''}{money(profit)} HAUL THIS WEEK
      </div>

      <div style={{ position: 'relative' }}>
        <motion.button
          type="button"
          onClick={sell}
          disabled={running}
          whileTap={{ y: 1, boxShadow: '0 0 0 1px #ffba2b66, 0 0 8px #ffba2b66' }}
          style={{
            width: '100%', padding: '15px 20px', borderRadius: 16,
            border: '2.5px solid #ffba2b', background: '#ffba2b', color: INK,
            boxShadow: '0 0 0 1px #ffba2b66, 0 0 22px #ffba2b77, 0 8px 20px rgba(0,0,0,0.5)',
            cursor: running ? 'default' : 'pointer', fontFamily: 'inherit',
            fontWeight: 900, fontSize: 19, opacity: running ? 0.7 : 1,
          }}
        >
          {running ? 'DROPPING…' : '🔥 DROP IT!'}
        </motion.button>

        {/* Instant payoff burst — coins fly up + a big "+B$X this week!" pop,
            right on the button, before the Payday card slides in. */}
        <AnimatePresence>
          {burst !== null && (
            <motion.div
              key="burst"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', overflow: 'visible' }}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 6, x: (i - 2) * 26, scale: 0.6 }}
                  animate={{ opacity: [0, 1, 1, 0], y: -46 - i * 4, scale: 1 }}
                  transition={{ duration: 1.1, delay: i * 0.06, ease: 'easeOut' }}
                  style={{ position: 'absolute', fontSize: 20 }}
                >
                  🪙
                </motion.span>
              ))}
              <motion.div
                initial={{ scale: 0.6, y: 4 }}
                animate={{ scale: [0.6, 1.12, 1], y: -4 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                style={{
                  position: 'relative', padding: '8px 16px', borderRadius: 14,
                  background: '#2fe08a', color: '#0b0a16', fontWeight: 900, fontSize: 18,
                  border: '2.5px solid #16131f', boxShadow: '0 0 22px #2fe08aaa',
                  whiteSpace: 'nowrap',
                }}
              >
                🎉 +{money(burst)} HAUL!
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function StepBtn({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.9 }}
      style={{
        width: 48, height: 48, borderRadius: 14, flexShrink: 0,
        border: `2.5px solid ${NEON}`, background: '#2e2456', color: TEXT,
        boxShadow: disabled ? 'none' : `0 0 12px ${NEON}55`,
        fontFamily: 'inherit', fontWeight: 900, fontSize: 26, lineHeight: 1,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </motion.button>
  );
}

/** Tiny check so this file stays self-contained — kept in case anything else imports it. */
export function _hqIsLegit() { return true; }
